import { usePathname } from 'next/navigation'
import Link from 'next/link'

import { isSamePath } from '@/utils/comparePaths'
import { DocsNavigation, navigation } from '@/components/DocsNavigation'
import { Prose } from '@/components/Prose'
import { TableOfContents, TOCSection } from '@/components/TableOfContents'
import { AuthorsMini, AuthorsFull } from '@/components/marketing/Authors'
import { authors } from '@/authors'
import { Author } from '@/components/marketing/Authors'
// import { FooterV2 } from '@/components/Footer'

interface BlogMeta {
  authorNames?: string[]
  authors?: Author[]
}

export function DocsLayout({
  children,
  title,
  tableOfContents,
  blogMeta,
}: {
  children: React.ReactNode
  title?: string
  tableOfContents?: TOCSection[]
  blogMeta: BlogMeta
}) {
  const pathname = usePathname()
  let section = pathname
    ? navigation.find((section) =>
        section.links.find((link) => isSamePath(link.href, pathname))
      )
    : null

  const isSnowflakeDeveloperGuide = pathname?.startsWith(
    '/docs/snowflake-developer-guide'
  )
  const isSnowflakeDeveloperPost =
    isSnowflakeDeveloperGuide && pathname !== '/docs/snowflake-developer-guide'

  if (!section && isSnowflakeDeveloperGuide) {
    section = navigation.find(
      (section) => section.id == 'snowflake-developer-guide'
    )
  }

  blogMeta.authorNames = blogMeta.authorNames || []
  blogMeta.authors = blogMeta.authorNames.map(
    (author_name) => authors[author_name as keyof typeof authors]
  )
  const authorsPresent = blogMeta?.authors?.length > 0


  return (
    <>
      <div className="max-w-9xl relative mx-auto mt-[-0px] flex justify-center sm:px-2 lg:px-8 xl:px-12">
        <div className="hidden border-r border-slate-800 lg:relative lg:block lg:flex-none">
          <div className="sticky top-16 -ml-0.5 h-[calc(100vh-4rem)] overflow-y-auto py-16 pl-0.5 pt-0">
            <DocsNavigation />
          </div>
        </div>

        <div className="min-w-0 max-w-2xl flex-auto px-4 py-16 pt-0 lg:max-w-none lg:pl-8 lg:pr-0 xl:px-14">
          <article>
            {(title || section) && (
              <header className="mb-9 space-y-1">
                {section && (
                  <p
                    id={section.id}
                    className={`font-display flex items-center gap-x-2 text-sm font-medium text-sky-500`}
                  >
                    {isSnowflakeDeveloperPost ? (
                      <Link href="/docs/snowflake-developer-guide">
                        &#8592; {section.title}
                      </Link>
                    ) : (
                      section.title
                    )}
                    {section.title == 'dbt-snowflake-monitoring' && (
                      <Link href="https://github.com/get-select/dbt-snowflake-monitoring">
                        {/* <GitHubIcon className="h-4 w-4 fill-sky-500 hover:fill-sky-400" /> */}
                      </Link>
                    )}
                  </p>
                )}
                {title && (
                  <h1 className="font-display text-3xl tracking-tight text-white">
                    {title}
                  </h1>
                )}
              </header>
            )}
            {authorsPresent && (
              <div className="my-6">
                <AuthorsMini authors={blogMeta.authors} />
              </div>
            )}
            <Prose className="">{children}</Prose>
          </article>
          {isSnowflakeDeveloperGuide && authorsPresent && (
            <AuthorsFull authors={blogMeta.authors} />
          )}
        </div>

        <TableOfContents
          title="On this page"
          tableOfContents={tableOfContents || []}
          width="w-48 custom-xl:w-60"
        />
      </div>
    </>
  )
}
